import type {
  TrainingEvalEpisodeView,
  TrainingEvalMetricsView,
  TrainingEvalSuccessTermsView,
  TrainingEvalTermView,
} from "@/api/trainingEval";
import { formatDateTime, formatDuration, parseUtcMs } from "@/utils/datetime";
import { boxStats, scalarTermValues, type BoxStats } from "./evalDistributionModel";

const SUCCESS_OUTCOMES = new Set(["true_success", "success"]);

export const SUCCESS_RATE_METRIC_KEYS = [
  "success_rate",
  "scenario_success_rate",
  "completion_rate",
] as const;

export function metricValue(
  metrics: TrainingEvalMetricsView | null,
  keys: readonly string[],
): number | null {
  if (metrics === null) return null;
  for (const key of keys) {
    const value = metrics.metrics_all[key] ?? metrics.metrics[key];
    if (typeof value === "number" && Number.isFinite(value)) return value;
  }
  return null;
}

export function formatDetailNumber(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return "-";
  if (Math.abs(value) >= 100) return value.toFixed(0);
  if (Math.abs(value) >= 10) return value.toFixed(1);
  return value.toFixed(3).replace(/0+$/, "").replace(/\.$/, "");
}

export function formatDetailDuration(seconds: number | null): string {
  return formatDuration(seconds === null ? null : seconds * 1000);
}

export function formatDetailDate(value: string | null): string {
  return formatDateTime(parseUtcMs(value));
}

export type OutcomeDatum = {
  label: string;
  count: number;
  color: string;
};

// 결과 색은 c6 status 램프에서 뜻에 맞게 고른다. 오탐은 경고, 시간 초과와 알 수 없는 결과는 중립이다.
// 하드코딩 hex 는 라이트와 다크에서 같은 값이라 다크에서 대비가 낮아진다.
const OUTCOME_META: Record<string, { label: string; color: string }> = {
  true_success: { label: "성공", color: "var(--c6-color-status-success)" },
  success: { label: "성공", color: "var(--c6-color-status-success)" },
  false_positive: { label: "오탐", color: "var(--c6-color-status-warning)" },
  failure: { label: "실패", color: "var(--c6-color-status-error)" },
  env_timeout: { label: "시간 초과", color: "var(--c6-color-status-muted)" },
};

export function isSuccessOutcome(outcome: string | null): boolean {
  return outcome !== null && SUCCESS_OUTCOMES.has(outcome);
}

export function episodeOutcomeMeta(outcome: string | null): { label: string; color: string } {
  const key = outcome ?? "unknown";
  return OUTCOME_META[key] ?? { label: key, color: "var(--c6-color-status-muted)" };
}

export function outcomeBreakdownFromEpisodes(
  episodes: readonly TrainingEvalEpisodeView[],
): OutcomeDatum[] {
  const counts = new Map<string, OutcomeDatum>();
  for (const episode of episodes) {
    const metadata = episodeOutcomeMeta(episode.outcome);
    const current = counts.get(metadata.label);
    counts.set(metadata.label, {
      label: metadata.label,
      count: (current?.count ?? 0) + 1,
      color: metadata.color,
    });
  }
  return [...counts.values()];
}

export function outcomeBreakdown(terms: TrainingEvalSuccessTermsView | null): OutcomeDatum[] {
  return terms === null ? [] : outcomeBreakdownFromEpisodes(terms.episodes);
}

/** 원시 값 또는 backend 5-number 요약을 detail box plot 계약으로 정규화한다. */
export function termBoxStats(term: TrainingEvalTermView): BoxStats | null {
  if (term.kind !== "scalar") return null;
  const values = scalarTermValues(term);
  if (values.length > 0) return boxStats(values);
  const stats = term.stats;
  if (stats === null) return null;
  const { min, q1, median, q3, max } = stats;
  if (min === null || q1 === null || median === null || q3 === null || max === null) return null;
  return { min, q1, median, q3, max, outliers: [], count: term.count };
}
